import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { AsyncSection, Icon } from "../AgentAnalytics/ds";
import tokens from "../AgentAnalytics/designTokens";
import { useTableStyles } from "../AgentAnalytics/tableStyles";
import { fetchAnalytics } from "./adTemplateApi";
import { freeMeter, int, money, relativeDay } from "./formatters";
import ProjectDrawer from "./ProjectDrawer";

export const PROJECT_COLUMNS = [
  { key: "assistantId", label: "Project" },
  { key: "clientId", label: "Client" },
  { key: "runs", label: "Prompts", numeric: true, sortable: true },
  { key: "adsGenerated", label: "Ads generated", numeric: true, sortable: true },
  { key: "imageAds", label: "Image ads", numeric: true },
  { key: "videoAds", label: "Video ads", numeric: true },
  { key: "freeLeft", label: "Free left" },
  { key: "spent", label: "Billed", numeric: true, sortable: true },
  { key: "freeValue", label: "Free value", numeric: true },
  { key: "liveAds", label: "Live ads", numeric: true, sortable: true },
  { key: "lastUsed", label: "Last used", sortable: true },
];

export const PROJECT_EXPORT_COLUMNS = [
  { key: "projectName", label: "Project name" },
  ...PROJECT_COLUMNS.map((c) =>
    c.key === "assistantId" ? { ...c, label: "Project ID" } : c
  ),
];

const useLocalStyles = makeStyles({
  centeredTable: {
    "& td, & th": { textAlign: "center" },
  },
  num: {
    textAlign: "center",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  liveAdsBtn: {
    border: "none",
    background: "none",
    padding: 0,
    cursor: "pointer",
    fontFamily: tokens.fontBody,
    fontSize: 13,
    fontWeight: 700,
    color: tokens.link,
    textDecoration: "underline",
    fontVariantNumeric: "tabular-nums",
    "&:hover": { color: tokens.primary },
  },
});

export default function ProjectsView({ range, search, band, refreshKey, onRows, format = "" }) {
  const classes = { ...useTableStyles(), ...useLocalStyles() };
  const [state, setState] = useState({
    loading: true,
    notFound: false,
    items: [],
    total: 0,
    error: null,
  });
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("lastUsed");
  const [dir, setDir] = useState("desc");
  const [selected, setSelected] = useState(null);
  const [drawerTab, setDrawerTab] = useState("runs");

  const openDrawer = (row, tab) => {
    setDrawerTab(tab);
    setSelected(row);
  };

  useEffect(() => {
    setPage(0);
  }, [range.from, range.to, band, search, format]);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    fetchAnalytics("projects", {
      from: range.from,
      to: range.to,
      page: page + 1,
      sort,
      dir,
      band,
      search,
      ...(format ? { format } : {}),
    }).then((r) => {
      if (!alive) return;
      const items = (r.data && r.data.items) || [];
      setState({
        loading: false,
        notFound: r.notFound,
        items,
        total: (r.data && r.data.total) || 0,
        error: r.error,
      });
      if (onRows) onRows(items, PROJECT_EXPORT_COLUMNS);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, page, sort, dir, band, search, format, refreshKey]);

  const toggleSort = (key) => {
    if (sort === key) {
      setDir(dir === "asc" ? "desc" : "asc");
      return;
    }
    setSort(key);
    setDir("desc");
  };

  return (
    <>
      <AsyncSection
        loading={state.loading}
        notFound={state.notFound}
        error={state.error}
        empty={!state.items.length}
        emptyTitle="No ad templates in this range"
        emptyDescription="No project generated ad templates here. Try a wider date range or a different filter."
      >
        <Box data-testid="projects-table">
          <div className={`${classes.tableWrap} ${classes.centeredTable}`}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {PROJECT_COLUMNS.map((c) => (
                    <TableCell
                      key={c.key}
                      align="center"
                      className={classes.headCell}
                      data-testid={`col-${c.key}`}
                    >
                      {c.sortable ? (
                        <TableSortLabel
                          className={classes.sortLabel}
                          active={sort === c.key}
                          direction={sort === c.key ? dir : "desc"}
                          onClick={() => toggleSort(c.key)}
                        >
                          {c.label}
                        </TableSortLabel>
                      ) : (
                        c.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.items.map((row) => (
                  <TableRow
                    key={row.assistantId}
                    hover
                    className={classes.clickRow}
                    data-testid={`project-row-${row.assistantId}`}
                    onClick={() => openDrawer(row, "runs")}
                  >
                    <TableCell className={classes.cell}>
                      <Typography component="div" className={classes.nameText}>
                        {row.projectName || row.assistantId}
                      </Typography>
                      {row.projectName ? (
                        <Typography component="div" className={classes.monoSub}>
                          {row.assistantId}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell className={classes.cell}>{row.clientId || "—"}</TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {int(row.runs)}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {int(row.adsGenerated)}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {int(row.imageAds)}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {int(row.videoAds)}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <span
                        style={{
                          color: row.freeLeft === 0 ? tokens.error : tokens.textPrimary,
                          fontWeight: row.freeLeft === 0 ? 700 : 400,
                        }}
                        data-testid={`free-left-${row.assistantId}`}
                      >
                        {freeMeter(row.freeLeft, row.freeLimit)}
                      </span>
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {money(row.spent, row.currency)}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {money(row.freeValue, row.currency)}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>
                      {row.liveAds > 0 ? (
                        <Tooltip title="See these ads and how they are performing">
                          <button
                            type="button"
                            className={classes.liveAdsBtn}
                            data-testid={`live-ads-${row.assistantId}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              openDrawer(row, "ads");
                            }}
                          >
                            {int(row.liveAds)}
                          </button>
                        </Tooltip>
                      ) : (
                        int(row.liveAds)
                      )}
                      {row.liveAds === 0 ? (
                        <Tooltip title="Generated ads but never launched one">
                          <span
                            data-testid={`never-launched-${row.assistantId}`}
                            style={{ marginLeft: 5, display: "inline-flex", verticalAlign: "middle" }}
                          >
                            <Icon
                              name="triangle-alert"
                              size={14}
                              style={{ color: tokens.pending }}
                            />
                          </span>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell className={classes.cell}>{relativeDay(row.lastUsed)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <TablePagination
            component="div"
            count={state.total}
            page={page}
            onChangePage={(event, next) => setPage(next)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        </Box>
      </AsyncSection>

      <ProjectDrawer
        project={selected}
        range={range}
        initialTab={drawerTab}
        onClose={() => setSelected(null)}
      />
    </>
  );
}
